import type * as api from '@gitbook/api-client';
import { toast } from '@gitbook/spine-react';
import { assertNever } from '@gitbook/util-ts';

import { apiClient, getIndexInSiteStructure, getSiblingsInSiteStructure } from '../../../state';

export async function moveSiteItem(props: {
    movedItem: api.SiteSection | api.SiteSectionGroup | api.SiteSpace;
    targetItem: api.SiteSection | api.SiteSectionGroup | api.SiteSpace;
    position: 'before' | 'after';
    organization: api.Organization;
    site: api.Site;
    siteStructure: api.SiteStructure;
}): Promise<void> {
    const { movedItem, targetItem, position, organization, site, siteStructure } = props;
    await toast.catch(async () => {
        const movedItemType = movedItem.object;
        switch (movedItemType) {
            case 'site-section-group': {
                if (
                    targetItem.object !== 'site-section-group' &&
                    targetItem.object !== 'site-section'
                ) {
                    throw new Error(
                        `Cannot move a site section group before/after a ${targetItem.object}`
                    );
                }
                break;
            }
            case 'site-section': {
                if (
                    targetItem.object !== 'site-section' &&
                    targetItem.object !== 'site-section-group'
                ) {
                    throw new Error(
                        `Cannot move a site section before/after a ${targetItem.object}`
                    );
                }
                break;
            }
            case 'site-space': {
                if (targetItem.object !== 'site-space') {
                    throw new Error(
                        `Cannot move a site variant before/after a ${targetItem.object}`
                    );
                }
                break;
            }
            default:
                assertNever(movedItemType);
        }
        await apiClient.sites.sortSiteStructure(organization.id, site.id, {
            item: {
                id: movedItem.id,
                type: movedItem.object,
            },
            position: getPositionData(targetItem, position, siteStructure),
        });
        toast.success('New position saved');
    }, 'Error while reordering site structure');
}

function getPositionData(
    targetItem: api.SiteSection | api.SiteSectionGroup | api.SiteSpace,
    position: 'before' | 'after',
    siteStructure: api.SiteStructure
) {
    const sibling = findSiblingItem(targetItem, position, siteStructure);
    if (position === 'after') {
        return {
            before: { type: targetItem.object, id: targetItem.id },
            after: sibling ? { type: sibling.object, id: sibling.id } : undefined,
        };
    }
    return {
        before: sibling ? { type: sibling.object, id: sibling.id } : undefined,
        after: { type: targetItem.object, id: targetItem.id },
    };
}

function findSiblingItem(
    targetItem: api.SiteSection | api.SiteSectionGroup | api.SiteSpace,
    position: 'before' | 'after',
    siteStructure: api.SiteStructure
) {
    const index = getIndexInSiteStructure(siteStructure, targetItem.id);
    if (index === -1) {
        return null;
    }
    const items = getSiblingsInSiteStructure(siteStructure, targetItem.id) ?? [];
    if (position === 'after') {
        return index < items.length - 1 ? items[index + 1] : null;
    }
    return index > 0 ? items[index - 1] : null;
}
